/**
 * Checkout endpoint (ecommerce-agnostic).
 *
 *   - POST /subscriptions/create-checkout
 *
 * ONE generic call for every platform: the frontend sends the canonical
 * selection + an optional `platform` hint and the backend resolves the effective
 * platform (Shopify / PrestaShop), resolves the per-channel product ids from the
 * channel bindings, and routes the checkout. The frontend never knows a store's
 * ids. Re-exported from `services/api.ts` so existing call-sites keep working.
 */

import axios from 'axios';
import { apiClient, type OutOfStockDetailPayload, toOutOfStockError, withRetry } from './client';
import type { ShippingDetectionSource } from '../types/config';

// ─── Shared payload shapes ──────────────────────────────────────────────────

export interface ExtraLineItem {
  variant_id: string;
  /** Default 1; the donation 1€-priced variant uses qty = euros for custom amounts. */
  quantity?: number;
  kind?: 'donation';
}

export interface ShippingPayload {
  country: string;
  postal_code: string;
  zone_id?: string | null;
  /** Frontend's calculated price. Backend recalculates and uses its own
   *  result — this is sent for telemetry / mismatch detection only. */
  price_estimated: number;
  detection_source?: ShippingDetectionSource;
}

export interface CheckoutRequest {
  calculation_id: string;
  email: string;
  dog_name: string;
  accepts_marketing: boolean;
  is_initiation_pack: boolean;
  /** Recipe distribution keyed by canonical product.id (UUID) → quantity. The
   *  backend resolves the per-channel variant from the channel bindings. */
  initiation_pack_distribution?: Record<string, number>;
  /** Selected supplements as canonical product.ids (UUIDs). */
  selected_supplement_ids?: string[];
  /** One-off extra lines added to the cart (currently donations). */
  extra_line_items?: ExtraLineItem[];
  /** Shipping selection from Step13 (only when shipping_rules.enabled). */
  shipping?: ShippingPayload;
  platform?: 'shopify' | 'prestashop';
  /** Browser session_id from useAnalytics — persisted on the lead for funnel attribution */
  analytics_session_id?: string | null;
  /**
   * Multi-pet checkout (v1). Total de mascotas en el pedido. Solo presente
   * cuando hay más de una mascota (petsCompleted.length + 1 > 1).
   * Adjuntado como atributo del carrito de Shopify para visibilidad de soporte.
   */
  num_mascotas?: number;
  /** JSON compacto con nombre y variantes de cada mascota. Máx 4000 chars. */
  mascotas_json?: string;
}

export interface CheckoutResponse {
  lead_id: string;
  checkout_url: string;
  discount_code: string;
}

// ─── Client-side checkout (prepare / callback) ──────────────────────────────
// Espejo campo-a-campo del contrato del backend (Pydantic, snake_case). Cuando
// `runtime_controls.checkout_client_side_enabled` está ON, `prepare-checkout`
// valida server-side y devuelve esta "spec"; el navegador resuelve el selling
// plan y hace el cartCreate. `checkout-callback` recupera la observabilidad.

/** Datos para que el navegador llame al Storefront API directamente. */
export interface StorefrontConfig {
  graphql_url: string;
  public_access_token: string;
  api_version: string;
}

/** Atributo key/value de carrito o de línea (Shopify solo acepta strings). */
export interface CartAttribute {
  key: string;
  value: string;
}

/**
 * Línea de suscripción por receta cuyo selling plan resuelve el navegador.
 * `quantity` YA viene resuelta (no recalcular). `monthly_discount` es null en
 * pedido completo (`resolve_monthly_plan=false`).
 */
export interface PrepareRecipeLine {
  variant_gid: string;
  variant_numeric: string;
  quantity: number;
  base_price: number;
  target_discount: number;
  monthly_discount: number | null;
  resolve_monthly_plan: boolean;
  line_attributes: CartAttribute[];
}

/** Línea one-off (suplemento / donación): sin selling plan. */
export interface PrepareOneoffLine {
  variant_gid: string;
  quantity: number;
  line_attributes: CartAttribute[];
}

/** Spec que el navegador usa para crear el carrito Shopify client-side. */
export interface PrepareCheckoutResponse {
  lead_id: string;
  buyer_email: string;
  storefront: StorefrontConfig;
  allowed_checkout_domains: string[];
  recipe_lines: PrepareRecipeLine[];
  oneoff_lines: PrepareOneoffLine[];
  /** Atributos de carrito YA construidos server-side EXCEPTO monthly_selling_plans. */
  cart_attributes: CartAttribute[];
}

/** Reporte best-effort del navegador tras intentar crear el carrito. */
export interface CheckoutCallbackRequest {
  lead_id: string;
  outcome: 'success' | 'failure';
  checkout_url?: string;
  cart_id?: string;
  error_code?: string;
  error_detail?: string;
}

// ─── Endpoints ──────────────────────────────────────────────────────────────

/**
 * Create checkout in Shopify. The optional idempotencyKey is sent as
 * X-Idempotency-Key — server uses it to dedupe retries with the same
 * payload (returns the same response) and reject conflicting payloads.
 *
 * Errores tipados:
 * - OutOfStockError (409, code='OUT_OF_STOCK'): la selección de
 *   recetas se quedó sin stock entre paso 11 y este checkout. El
 *   frontend debe rebotar a step 11.
 */
export const createCheckout = async (
  checkoutData: CheckoutRequest,
  idempotencyKey?: string,
): Promise<CheckoutResponse> => {
  return withRetry(async () => {
    const config: any = {};
    if (idempotencyKey) {
      config.headers = { 'X-Idempotency-Key': idempotencyKey };
    }

    try {
      const response = await apiClient.post<CheckoutResponse>('/subscriptions/create-checkout', checkoutData, config);
      return response.data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error creating checkout:', error);

      if (axios.isAxiosError(error)) {
        const detail = error.response?.data?.detail;
        const status = error.response?.status;
        const detailPayload = error.response?.data?.detail as OutOfStockDetailPayload | undefined;

        if (status === 409 && detailPayload?.code === 'OUT_OF_STOCK') {
          throw toOutOfStockError(detailPayload, status);
        }

        if (status === 422) {
          const detailStr = typeof detail === 'string' ? detail : JSON.stringify(detail);
          throw new Error(`Error de validación: ${detailStr}`);
        }

        if (typeof detail === 'string' && detail.trim()) {
          throw new Error(`Error checkout (${status}): ${detail}`);
        }
      }
      throw new Error('No se pudo crear el checkout. Por favor, inténtalo de nuevo.');
    }
  });
};

/**
 * Prepara un checkout client-side (flag runtime_controls.checkout_client_side_enabled).
 *
 * Mismo body/cabecera/errores que `createCheckout`, pero el backend NO llama a
 * Shopify: devuelve la "spec" para que el navegador resuelva el sellingPlanId y
 * haga el cartCreate desde la IP del comprador.
 *
 * Errores tipados (idénticos a createCheckout + 503):
 * - OutOfStockError (409, code='OUT_OF_STOCK') → rebote a selección de recetas.
 * - 503 (token no público) → "checkout no disponible".
 * - 422 (body inválido) / detail string / genérico.
 */
export const prepareCheckout = async (
  checkoutData: CheckoutRequest,
  idempotencyKey?: string,
): Promise<PrepareCheckoutResponse> => {
  return withRetry(async () => {
    const config: any = {};
    if (idempotencyKey) {
      config.headers = { 'X-Idempotency-Key': idempotencyKey };
    }

    try {
      const response = await apiClient.post<PrepareCheckoutResponse>(
        '/subscriptions/prepare-checkout',
        checkoutData,
        config,
      );
      return response.data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error preparing checkout:', error);

      if (axios.isAxiosError(error)) {
        const detail = error.response?.data?.detail;
        const status = error.response?.status;
        const detailPayload = error.response?.data?.detail as OutOfStockDetailPayload | undefined;

        if (status === 409 && detailPayload?.code === 'OUT_OF_STOCK') {
          throw toOutOfStockError(detailPayload, status);
        }

        if (status === 503) {
          throw new Error('El checkout no está disponible temporalmente. Inténtalo de nuevo en unos minutos.');
        }

        if (status === 422) {
          const detailStr = typeof detail === 'string' ? detail : JSON.stringify(detail);
          throw new Error(`Error de validación: ${detailStr}`);
        }

        if (typeof detail === 'string' && detail.trim()) {
          throw new Error(`Error checkout (${status}): ${detail}`);
        }
      }
      throw new Error('No se pudo preparar el checkout. Por favor, inténtalo de nuevo.');
    }
  });
};

/**
 * Reporta el resultado del cartCreate client-side (best-effort). SIN withRetry:
 * el backend SIEMPRE responde 200 {ok:true} y esta llamada NUNCA lanza — la
 * telemetría jamás debe romper el checkout del cliente.
 */
export const checkoutCallback = async (body: CheckoutCallbackRequest): Promise<void> => {
  try {
    await apiClient.post('/subscriptions/checkout-callback', body);
  } catch {
    /* swallow: best-effort, nunca rompe el checkout */
  }
};
